import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useReportsHubBack } from '../../reports/hub/useReportsHubBack';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { useNavigate } from 'react-router-dom';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import AccountingDataTable, { DATATABLE_STYLES } from '../shared/AccountingDataTable';
import GuideButton from '../../common/guide/GuideButton';
import type { DataTableColumn } from '../shared/AccountingDataTable';

interface InvoiceReportItem {
  id: string;
  invoiceNumber: string;
  invoiceType: 'SALES' | 'PURCHASE' | 'QUICK_SALE';
  accountName: string;
  invoiceDate: string;
  dueDate: string;
  grandTotal: number;
  currency: string;
  isDeleted: boolean;
  deletedAt?: string;
  createdAt: string;
}

const InvoiceReports: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { goBack } = useReportsHubBack('/fatura/raporlar', () => navigate('/fatura/satis'));
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const pickerLocale = isTr ? tr : enUS;
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<InvoiceReportItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [sortColumn, setSortColumn] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filtre state'leri
  const [showFilters, setShowFilters] = useState(true);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [invoiceType, setInvoiceType] = useState<string>('ALL');
  const [includeDeleted, setIncludeDeleted] = useState<string>('ALL');
  
  // Sayfalama
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Excel indirme
  const [loadingExport, setLoadingExport] = useState(false);

  // Company ID'yi yükle
  React.useEffect(() => {
    const loadCompanyId = async () => {
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        setError(tt('Kullanıcı oturumu açık değil.', 'User session is not active.'));
        return;
      }

      try {
        const userData = await firebaseDataService.getUserData(uid);
        if (userData?.companyId) {
          setCompanyId(userData.companyId);
        } else {
          setError(tt('Şirket bilgisi bulunamadı.', 'Company information not found.'));
        }
      } catch (err) {
        console.error('Company ID yüklenirken hata:', err);
        setError(tt('Şirket bilgileri yüklenirken bir hata oluştu.', 'An error occurred while loading company data.'));
      }
    };

    loadCompanyId();
  }, []);

  // Faturaları server-side sayfalama ile yükle
  const loadReports = useCallback(async () => {
    if (!companyId || !startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      const response = await accountingApi.get(
        ACCOUNTING_API.INVOICE_REPORTS_PAGED({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          page: page + 1,
          pageSize: rowsPerPage,
          includeDeleted: includeDeleted === 'ALL' ? 'true' : includeDeleted === 'DELETED' ? 'only' : 'false',
          invoiceType: invoiceType !== 'ALL' ? invoiceType : undefined,
          searchTerm: searchTerm || undefined,
          sortColumn: sortColumn || undefined,
          sortDirection: sortDirection || 'desc',
        })
      );

      const result = response.data;
      setInvoices(result.data || []);
      setTotalCount(result.totalCount || 0);
    } catch (err) {
      console.error('Fatura raporları yüklenirken hata:', err);
      setError(tt('Fatura raporları yüklenirken bir hata oluştu.', 'An error occurred while loading invoice reports.'));
    } finally {
      setLoading(false);
    }
  }, [companyId, startDate, endDate, page, rowsPerPage, includeDeleted, invoiceType, searchTerm, sortColumn, sortDirection]);

  // Filtre butonu ile ilk yükleme tetiklenir
  const [filterTriggered, setFilterTriggered] = useState(false);

  const handleFilter = () => {
    if (!startDate || !endDate) {
      setError(tt('Lütfen başlangıç ve bitiş tarihlerini seçin.', 'Please select start and end dates.'));
      return;
    }
    setPage(0);
    setFilterTriggered(true);
  };

  // Debounce ile server-side yükleme
  useEffect(() => {
    if (!filterTriggered || !companyId || !startDate || !endDate) return;
    const timer = setTimeout(() => {
      loadReports();
    }, 400);
    return () => clearTimeout(timer);
  }, [loadReports, filterTriggered]);

  // Filtre değiştiğinde sayfa sıfırla
  useEffect(() => {
    setPage(0);
  }, [searchTerm, sortColumn, sortDirection]);

  // Sayfalanmış faturalar (server tarafından sayfalanmış)
  const paginatedInvoices = invoices;

  // AccountingDataTable kolon tanımları
  const reportColumns = useMemo<DataTableColumn<InvoiceReportItem>[]>(() => [
    { id: 'invoiceNumber', label: tt('Fatura No', 'Invoice No'), field: 'invoiceNumber', sortable: true },
    {
      id: 'invoiceType',
      label: tt('Tür', 'Type'),
      sortable: true,
      render: (row) => (
        <Chip
          icon={getInvoiceTypeIcon(row.invoiceType)}
          label={getInvoiceTypeName(row.invoiceType)}
          color={getInvoiceTypeColor(row.invoiceType)}
          size="small"
          variant="outlined"
        />
      ),
    },
    { id: 'accountName', label: tt('Cari Hesap', 'Account'), field: 'accountName', sortable: true },
    {
      id: 'invoiceDate',
      label: tt('Fatura Tarihi', 'Invoice Date'),
      field: 'invoiceDate',
      sortable: true,
      render: (row) => <>{formatDate(row.invoiceDate)}</>,
    },
    {
      id: 'grandTotal',
      label: tt('Toplam Tutar', 'Grand Total'),
      field: 'grandTotal',
      sortable: true,
      render: (row) => <>{formatCurrency(row.grandTotal, row.currency)}</>,
    },
    {
      id: 'isDeleted',
      label: tt('Durum', 'Status'),
      sortable: true,
      render: (row) => row.isDeleted
        ? <Chip icon={<DeleteOutlineIcon fontSize="small" />} label={tt('Silindi', 'Deleted')} color="error" size="small" variant="outlined" />
        : <Chip label={tt('Aktif', 'Active')} color="success" size="small" variant="outlined" />,
    },
    {
      id: 'createdAt',
      label: tt('Oluşturma Tarihi', 'Created At'),
      field: 'createdAt',
      sortable: true,
      render: (row) => <>{formatDate(row.createdAt)}</>,
    },
  ], [isTr]);

  // Sayfa değişimi
  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Fatura tipi adı
  const getInvoiceTypeName = (type: string): string => {
    switch (type) {
      case 'SALES': return tt('Satış', 'Sales');
      case 'PURCHASE': return tt('Alış', 'Purchase');
      case 'QUICK_SALE': return tt('Hızlı Satış', 'Quick Sale');
      default: return type;
    }
  };

  // Fatura tipi rengi
  const getInvoiceTypeColor = (type: string): 'primary' | 'secondary' | 'info' => {
    switch (type) {
      case 'SALES': return 'primary';
      case 'PURCHASE': return 'secondary';
      case 'QUICK_SALE': return 'info';
      default: return 'primary';
    }
  };

  // Fatura tipi ikonu
  const getInvoiceTypeIcon = (type: string) => {
    switch (type) {
      case 'PURCHASE': return <ShoppingCartIcon fontSize="small" />;
      case 'QUICK_SALE': return <PointOfSaleIcon fontSize="small" />;
      default: return <ShoppingBagIcon fontSize="small" />;
    }
  };

  // Para formatı
  const formatCurrency = (amount: number, currencyCode: string): string => {
    return new Intl.NumberFormat(localeCode, {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  };

  // Tarih formatı
  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return new Intl.DateTimeFormat(localeCode, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      return '-';
    }
  };

  // Excel'e aktarma
  const exportToExcel = async () => {
    try {
      setLoadingExport(true);

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(tt('Fatura Raporu', 'Invoice Report'));

      // Başlık satırı
      worksheet.mergeCells('A1:I1');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = tt('Fatura Raporu', 'Invoice Report');
      titleCell.style = {
        font: { bold: true, size: 14 },
        alignment: { horizontal: 'center' }
      };

      worksheet.addRow([]);

      // Tablo başlıkları
      const headerRow = worksheet.addRow([
        tt('Fatura No', 'Invoice No'),
        tt('Tür', 'Type'),
        tt('Cari Hesap', 'Account'),
        tt('Fatura Tarihi', 'Invoice Date'),
        tt('Vade Tarihi', 'Due Date'),
        tt('Para Birimi', 'Currency'),
        tt('Toplam Tutar', 'Grand Total'),
        tt('Durum', 'Status'),
        tt('Oluşturma Tarihi', 'Created At')
      ]);

      headerRow.eachCell((cell) => {
        cell.style = {
          font: { bold: true, color: { argb: 'FFFFFFFF' } },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } },
          border: {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          }
        };
      });

      // Veri satırları
      invoices.forEach((invoice) => {
        worksheet.addRow([
          invoice.invoiceNumber,
          getInvoiceTypeName(invoice.invoiceType),
          invoice.accountName,
          formatDate(invoice.invoiceDate),
          formatDate(invoice.dueDate),
          invoice.currency,
          invoice.grandTotal,
          invoice.isDeleted ? tt('Silindi', 'Deleted') : tt('Aktif', 'Active'),
          formatDate(invoice.createdAt)
        ]);
      });

      // Sütun genişlikleri
      worksheet.getColumn(1).width = 15;
      worksheet.getColumn(2).width = 12;
      worksheet.getColumn(3).width = 30;
      worksheet.getColumn(4).width = 18;
      worksheet.getColumn(5).width = 18;
      worksheet.getColumn(6).width = 12;
      worksheet.getColumn(7).width = 15;
      worksheet.getColumn(8).width = 12;
      worksheet.getColumn(9).width = 18;

      worksheet.getColumn(7).numFmt = '#,##0.00';

      // Zebra stripe
      for (let i = 4; i <= worksheet.rowCount; i++) {
        const fillColor = i % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
        worksheet.getRow(i).eachCell(cell => {
          cell.style = {
            ...cell.style,
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } },
            border: {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            }
          };
        });
      }

      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `${tt('Fatura_Raporu', 'Invoice_Report')}_${currentDate}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);

      console.log('Excel dışa aktarma tamamlandı.');
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      setError(tt('Excel dışa aktarma sırasında bir hata oluştu.', 'An error occurred during Excel export.'));
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <Box sx={{
      p: { xs: 2, md: 3 },
      overflowY: 'auto',
      height: 'calc(100vh - 64px)',
      '&::-webkit-scrollbar': { width: '8px' },
      '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
      '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '4px' },
      '&::-webkit-scrollbar-thumb:hover': { background: '#555' },
    }}>
      {/* Başlık ve Butonlar */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: 1,
        mb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={goBack}
            size="small"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark'
              }
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <AssessmentIcon sx={{ fontSize: { xs: 24, md: 28 }, color: 'primary.main' }} />
          <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold">
            {tt('Fatura Raporları', 'Invoice Reports')}
          </Typography>
          <GuideButton guideKey="documents.invoiceReports" size="small" />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? tt('Filtreleri Gizle', 'Hide Filters') : tt('Filtreleri Göster', 'Show Filters')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={exportToExcel}
            disabled={loadingExport || invoices.length === 0}
          >
            {loadingExport ? tt('Hazırlanıyor...', 'Preparing...') : tt('Excel İndir', 'Download Excel')}
          </Button>
        </Box>
      </Box>

      {/* Filtreler */}
      {showFilters && (
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pickerLocale}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={3}>
                <DateTimePicker
                  label={tt('Başlangıç Tarihi', 'Start Date')}
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DateTimePicker
                  label={tt('Bitiş Tarihi', 'End Date')}
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>{tt('Fatura Tipi', 'Invoice Type')}</InputLabel>
                  <Select
                    value={invoiceType}
                    label={tt('Fatura Tipi', 'Invoice Type')}
                    onChange={(e) => setInvoiceType(e.target.value)}
                  >
                    <MenuItem value="ALL">{tt('Tümü', 'All')}</MenuItem>
                    <MenuItem value="SALES">{tt('Satış', 'Sales')}</MenuItem>
                    <MenuItem value="PURCHASE">{tt('Alış', 'Purchase')}</MenuItem>
                    <MenuItem value="QUICK_SALE">{tt('Hızlı Satış', 'Quick Sale')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>{tt('Durum', 'Status')}</InputLabel>
                  <Select
                    value={includeDeleted}
                    label={tt('Durum', 'Status')}
                    onChange={(e) => setIncludeDeleted(e.target.value)}
                  >
                    <MenuItem value="ALL">{tt('Tümü', 'All')}</MenuItem>
                    <MenuItem value="ACTIVE">{tt('Sadece Aktif', 'Only Active')}</MenuItem>
                    <MenuItem value="DELETED">{tt('Sadece Silinen', 'Only Deleted')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  size="medium"
                  onClick={handleFilter}
                  disabled={loading || !startDate || !endDate}
                >
                  {loading ? <CircularProgress size={24} /> : tt('Filtrele', 'Filter')}
                </Button>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Paper>
      )}

      {/* Hata Mesajı */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Ana İçerik */}
      {loading ? (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px'
        }}>
          <CircularProgress />
        </Box>
      ) : invoices.length === 0 ? (
        <Box sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
          borderRadius: 2
        }}>
          <ReceiptIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" component="h2" gutterBottom color="primary.main" fontWeight="bold">
            {tt('Fatura Raporu', 'Invoice Report')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {tt('Rapor görüntülemek için tarih aralığı seçip "Filtrele" butonuna basın.', 'Select a date range and click "Filter" to view the report.')}
          </Typography>
        </Box>
      ) : (
        <>
          {/* Desktop: AccountingDataTable */}
          {!isMobile && (
            <AccountingDataTable<InvoiceReportItem>
              columns={reportColumns}
              rows={invoices}
              getRowId={(row) => row.id}
              loading={loading}
              serverSidePagination={true}
              totalCount={totalCount}
              page={page}
              pageSize={rowsPerPage}
              onPageChange={(newPage, newPageSize) => {
                setPage(newPage);
                setRowsPerPage(newPageSize);
              }}
              onSort={(field, direction) => {
                setSortColumn(field);
                setSortDirection(direction);
              }}
              sortField={sortColumn}
              sortDirection={sortDirection}
              emptyMessage={tt('Fatura bulunamadı.', 'No invoices found.')}
              emptyIcon={<ReceiptIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
              ariaLabel={tt('fatura raporları tablosu', 'invoice reports table')}
              columnResizeStorageKey="invoice-reports"
            />
          )}

          {/* Mobile: Card View */}
          {isMobile && (
            <Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                {paginatedInvoices.map((invoice) => (
                  <Card key={invoice.id} elevation={2}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ReceiptIcon color="primary" fontSize="small" />
                          <Typography variant="subtitle2" fontWeight="bold">
                            {invoice.invoiceNumber}
                          </Typography>
                        </Box>
                        <Chip
                          label={getInvoiceTypeName(invoice.invoiceType)}
                          color={getInvoiceTypeColor(invoice.invoiceType)}
                          size="small"
                          variant="outlined"
                        />
                      </Box>

                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">{tt('Cari Hesap:', 'Account:')}</Typography>
                        <Typography variant="body2">{invoice.accountName}</Typography>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">{tt('Fatura Tarihi:', 'Invoice Date:')}</Typography>
                          <Typography variant="body2">{formatDate(invoice.invoiceDate)}</Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">{tt('Durum:', 'Status:')}</Typography>
                          <Box>
                            {invoice.isDeleted ? (
                              <Chip label={tt('Silindi', 'Deleted')} color="error" size="small" />
                            ) : (
                              <Chip label={tt('Aktif', 'Active')} color="success" size="small" />
                            )}
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{
                        mt: 2,
                        pt: 1.5,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">{tt('Oluşturma:', 'Created:')}</Typography>
                          <Typography variant="body2">{formatDate(invoice.createdAt)}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary">{tt('Toplam:', 'Total:')}</Typography>
                          <Typography variant="h6" fontWeight="bold" color="primary.main">
                            {formatCurrency(invoice.grandTotal, invoice.currency)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage=""
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
                sx={{
                  '.MuiTablePagination-toolbar': { paddingLeft: 1, paddingRight: 1 },
                  '.MuiTablePagination-selectLabel': { display: 'none' },
                  '.MuiTablePagination-displayedRows': { fontSize: '0.875rem' }
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default InvoiceReports;

